import { Parameter } from '@shared/types';
import { calculateParameterRange } from '@/utils/parameterUtils';
import {
  getColorBrightness,
  generateHarmoniousColors,
  RGB,
} from '@/utils/colorUtils';

/**
 * Information about a color parameter for harmonious generation
 */
interface ColorParamInfo {
  index: number; // Index in the parameters array
  brightness: number; // Original brightness (0-1)
  hasAlpha: boolean; // Whether the color has an alpha channel
  originalAlpha: number; // Original alpha value if present
  originalRgb: RGB; // Original RGB for strength lerping
}

export interface RandomizeOptions {
  /**
   * How strongly to randomize values, from 0 (no change) to 1 (fully random).
   * For numbers/colors: lerps between the current value and a fully random value.
   * For booleans/options: probability of the value changing equals `strength`.
   * Defaults to 1 (full randomization).
   */
  strength?: number;
  /**
   * Set of parameter names that should not be randomized.
   */
  lockedParams?: Set<string>;
}

/**
 * Extracts color parameters and their brightness levels from the parameter list
 */
function extractColorInfo(parameters: Parameter[]): ColorParamInfo[] {
  const colorInfo: ColorParamInfo[] = [];

  parameters.forEach((param, index) => {
    if (param.type === 'color') {
      const colorVal = param.value as number[];
      if (colorVal && colorVal.length >= 3) {
        // Detect if using 0-255 range and normalize to 0-1
        const is255 = colorVal.some((n) => n > 1);
        const r = is255 ? colorVal[0] / 255 : colorVal[0];
        const g = is255 ? colorVal[1] / 255 : colorVal[1];
        const b = is255 ? colorVal[2] / 255 : colorVal[2];

        colorInfo.push({
          index,
          brightness: getColorBrightness(r, g, b),
          hasAlpha: colorVal.length === 4,
          originalAlpha: colorVal.length === 4 ? colorVal[3] : 1,
          originalRgb: { r, g, b },
        });
      }
    }
  });

  return colorInfo;
}

/**
 * Converts RGB (0-1) to OpenSCAD color array format
 */
function rgbToScadColor(rgb: RGB, hasAlpha: boolean, alpha: number): number[] {
  const format = (n: number) => Math.round(n * 1000) / 1000;

  if (hasAlpha) {
    return [format(rgb.r), format(rgb.g), format(rgb.b), format(alpha)];
  }
  return [format(rgb.r), format(rgb.g), format(rgb.b)];
}

/** Linearly interpolates between a and b by t (0=a, 1=b) */
function lerp(a: number, b: number, t: number): number {
  return a + t * (b - a);
}

export function randomizeParameters(
  parameters: Parameter[],
  options: RandomizeOptions = {},
): Parameter[] {
  const strength = options.strength ?? 1;
  const lockedParams = options.lockedParams ?? new Set<string>();

  // First, extract information about all *unlocked* color parameters
  const allColorInfo = extractColorInfo(parameters);
  // Only generate harmonious colors for unlocked color params
  const unlockedColorInfo = allColorInfo.filter(
    (info) => !lockedParams.has(parameters[info.index].name),
  );
  const colorCount = unlockedColorInfo.length;

  // Generate harmonious colors if there are any unlocked color parameters
  let harmoniousColors: RGB[] = [];
  if (colorCount > 0) {
    const originalBrightnesses = unlockedColorInfo.map(
      (info) => info.brightness,
    );
    harmoniousColors = generateHarmoniousColors(
      colorCount,
      originalBrightnesses,
    );
  }

  // Map from original param index → harmonious color index
  const paramIndexToColorIndex = new Map<number, number>();
  unlockedColorInfo.forEach((info, i) => {
    paramIndexToColorIndex.set(info.index, i);
  });

  return parameters.map((param, paramIndex) => {
    // Skip locked parameters entirely
    if (lockedParams.has(param.name)) {
      return param;
    }

    let newValue = param.value;

    if (param.options && param.options.length > 0) {
      // At full strength always pick a random option.
      // At lower strength there is a (1 - strength) chance to keep the current value.
      if (Math.random() < strength) {
        const randomIndex = Math.floor(Math.random() * param.options.length);
        newValue = param.options[randomIndex].value;
      }
    } else if (param.type === 'boolean') {
      // Probability of flipping equals strength
      if (Math.random() < strength) {
        newValue = Math.random() < 0.5;
      }
    } else if (param.type === 'number') {
      // Random number within range, lerped toward current value by strength
      const { min, max } = calculateParameterRange(param);
      const step =
        typeof param.range?.step === 'number' ? param.range.step : undefined;

      const fullyRandomVal = Math.random() * (max - min) + min;
      const currentVal =
        typeof param.value === 'number' ? param.value : (min + max) / 2;
      const rawVal = lerp(currentVal, fullyRandomVal, strength);

      if (step) {
        const steps = Math.round((rawVal - min) / step);
        newValue = Math.min(max, Math.max(min, min + steps * step));
      } else {
        newValue = Math.round(Math.min(max, Math.max(min, rawVal)) * 100) / 100;
      }
    } else if (param.type === 'stack') {
      // Randomize a stack: pick a random length within min/max bounds, then
      // fill with random choices from the allowed options.
      const stackOpts = param.stackConfig?.options ?? [];
      if (stackOpts.length > 0) {
        const currentLen = Array.isArray(param.value) ? param.value.length : 1;
        const minLen = param.stackConfig?.min ?? 1;
        // Max: use configured max, or grow by up to 2 relative to current length
        const maxLen = param.stackConfig?.max ?? Math.max(currentLen + 2, minLen + 2);
        const targetLen =
          strength >= 1
            ? Math.floor(Math.random() * (maxLen - minLen + 1)) + minLen
            : // At lower strength: blend — keep current length most of the time
              Math.random() < strength * 0.5
              ? Math.floor(Math.random() * (maxLen - minLen + 1)) + minLen
              : Math.max(minLen, Math.min(maxLen, currentLen));

        const currentItems = Array.isArray(param.value)
          ? (param.value as string[])
          : [];

        const nextItems: string[] = [];
        for (let i = 0; i < targetLen; i++) {
          if (i < currentItems.length && Math.random() >= strength) {
            // Keep existing item (probability 1 - strength)
            nextItems.push(currentItems[i]);
          } else {
            // Pick a random option
            nextItems.push(stackOpts[Math.floor(Math.random() * stackOpts.length)]);
          }
        }
        newValue = nextItems;
      }
    } else if (param.type === 'color') {
      const colorIdx = paramIndexToColorIndex.get(paramIndex);
      const info = allColorInfo.find((c) => c.index === paramIndex);

      if (
        info !== undefined &&
        colorIdx !== undefined &&
        colorIdx < harmoniousColors.length
      ) {
        const harmoniousRgb = harmoniousColors[colorIdx];
        // Lerp each channel from original toward the harmonious target
        const blendedRgb: RGB = {
          r: lerp(info.originalRgb.r, harmoniousRgb.r, strength),
          g: lerp(info.originalRgb.g, harmoniousRgb.g, strength),
          b: lerp(info.originalRgb.b, harmoniousRgb.b, strength),
        };
        newValue = rgbToScadColor(
          blendedRgb,
          info.hasAlpha,
          info.originalAlpha,
        );
      } else {
        // Fallback: shouldn't happen, but just in case
        const originalVal = param.value as number[];
        const r = Math.round(Math.random() * 100) / 100;
        const g = Math.round(Math.random() * 100) / 100;
        const b = Math.round(Math.random() * 100) / 100;
        if (originalVal.length === 4) {
          newValue = [r, g, b, originalVal[3]];
        } else {
          newValue = [r, g, b];
        }
      }
    }

    return {
      ...param,
      value: newValue,
    };
  });
}
